//
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import type { AccountRole, Class, Doc, Domain, Mixin, Permission, Ref, Space } from '@hcengineering/core'
import type { Asset, IntlString, Plugin, Resource } from '@hcengineering/platform'
import { plugin } from '@hcengineering/platform'
import type { AnyComponent, ComponentExtensionId } from '@hcengineering/ui'
import type { Action, ActionCategory, Viewlet, ViewActionAvailabilityFunction } from '@hcengineering/view'

import type {
  Compensation,
  Department,
  EmployeePosition,
  FxRate,
  Level,
  MaskSetting,
  MyCompensation,
  TeamKpi,
  Payout,
  Position,
  Role,
  Team,
  TeamMember,
  QuarterScore,
  SpotBonus,
  FinanceAccount,
  Transaction,
  TransactionCategory,
  MyReceipt,
  Receipt,
  PayoutMark,
  WebappRecord
} from './types'

export * from './types'
export * from './calc'
export * from './payroll'
export * from './ledger'
export * from './books'
export * from './kpiScore'

/**
 * @public
 */
export const kpiId = 'kpi' as Plugin

/**
 * @public
 */
export const DOMAIN_KPI = 'kpi' as Domain

/**
 * @public
 */
const kpi = plugin(kpiId, {
  app: {
    KPI: '' as Ref<Doc>
  },
  space: {
    // Admin-only. Private space holding every writable compensation record.
    Compensation: '' as Ref<Space>,
    // Reference data. A SystemSpace, so readable by all; writes gated by role.
    Catalog: '' as Ref<Space>
  },
  class: {
    Level: '' as Ref<Class<Level>>,
    Position: '' as Ref<Class<Position>>,
    Role: '' as Ref<Class<Role>>,
    Team: '' as Ref<Class<Team>>,
    TeamMember: '' as Ref<Class<TeamMember>>,
    Department: '' as Ref<Class<Department>>,
    FxRate: '' as Ref<Class<FxRate>>,
    Compensation: '' as Ref<Class<Compensation>>,
    QuarterScore: '' as Ref<Class<QuarterScore>>,
    SpotBonus: '' as Ref<Class<SpotBonus>>,
    Payout: '' as Ref<Class<Payout>>,
    MyCompensation: '' as Ref<Class<MyCompensation>>,
    TeamKpi: '' as Ref<Class<TeamKpi>>,
    FinanceAccount: '' as Ref<Class<FinanceAccount>>,
    TransactionCategory: '' as Ref<Class<TransactionCategory>>,
    Transaction: '' as Ref<Class<Transaction>>,
    Receipt: '' as Ref<Class<Receipt>>,
    MyReceipt: '' as Ref<Class<MyReceipt>>,
    MaskSetting: '' as Ref<Class<MaskSetting>>,
    PayoutMark: '' as Ref<Class<PayoutMark>>,
    WebappRecord: '' as Ref<Class<WebappRecord>>
  },
  mixin: {
    EmployeePosition: '' as Ref<Mixin<EmployeePosition>>
  },
  ids: {
    DefaultFxRate: '' as Ref<FxRate>,
    /**
     * Bayram puli: holiday money given to employees. An expense category of
     * the `holiday` component: never pay, so the money is not counted as
     * salary or as an overpayment. Created by the `holiday-pay-v1` migration.
     */
    HolidayPayCategory: '' as Ref<TransactionCategory>,
    /**
     * The expense categories of the quarter bonus and premiya components,
     * created by the `components-v1` migration (decision 11). The salary
     * category keeps the id it had: whichever category carried the old
     * payroll flag became the salary component.
     */
    QuarterBonusCategory: '' as Ref<TransactionCategory>,
    PremiyaCategory: '' as Ref<TransactionCategory>
  },
  icon: {
    KPI: '' as Asset,
    Level: '' as Asset,
    Position: '' as Asset,
    Money: '' as Asset,
    Payout: '' as Asset,
    Account: '' as Asset,
    Expense: '' as Asset,
    Receipt: '' as Asset,
    Department: '' as Asset,
    Budget: '' as Asset
  },
  category: {
    KPI: '' as Ref<ActionCategory>
  },
  permission: {
    ManageCatalog: '' as Ref<Permission>,
    EditCompensation: '' as Ref<Permission>,
    ApproveReceipts: '' as Ref<Permission>,
    ManageFinance: '' as Ref<Permission>
  },
  action: {
    DeleteLevel: '' as Ref<Action>,
    DeletePosition: '' as Ref<Action>,
    DeleteRole: '' as Ref<Action>,
    DeleteTeam: '' as Ref<Action>,
    DeleteDepartment: '' as Ref<Action>,
    DeleteCompensation: '' as Ref<Action>,
    DeleteQuarterScore: '' as Ref<Action>,
    DeleteSpotBonus: '' as Ref<Action>,
    DeletePayout: '' as Ref<Action>,
    DeleteReceipt: '' as Ref<Action>,
    DeleteMyReceipt: '' as Ref<Action>,
    DeleteFinanceAccount: '' as Ref<Action>,
    DeleteTransactionCategory: '' as Ref<Action>,
    DeleteTransaction: '' as Ref<Action>,
    DeleteTeamMember: '' as Ref<Action>,
    EditCompensation: '' as Ref<Action>
  },
  function: {
    /** Settings › Members changed someone's access level: their platform role follows at once. */
    AlignPlatformRole: '' as Resource<(personUuid: string, level: AccountRole) => Promise<void>>,
    CanDeleteLevel: '' as Resource<ViewActionAvailabilityFunction<Level>>,
    CanDeletePosition: '' as Resource<ViewActionAvailabilityFunction<Position>>,
    CanDeleteRole: '' as Resource<ViewActionAvailabilityFunction<Role>>,
    CanDeleteTeam: '' as Resource<ViewActionAvailabilityFunction<Team>>,
    CanDeleteDepartment: '' as Resource<ViewActionAvailabilityFunction<Department>>,
    CanDeleteCompensation: '' as Resource<ViewActionAvailabilityFunction<Compensation>>,
    CanNeverDelete: '' as Resource<ViewActionAvailabilityFunction<Doc>>,
    CanDeleteFinanceAccount: '' as Resource<ViewActionAvailabilityFunction<FinanceAccount>>,
    CanDeleteTransactionCategory: '' as Resource<ViewActionAvailabilityFunction<TransactionCategory>>,
    CanDeleteTransaction: '' as Resource<ViewActionAvailabilityFunction<Transaction>>,
    CanDeleteTeamMember: '' as Resource<ViewActionAvailabilityFunction<TeamMember>>,
    CanWithdrawMyReceipt: '' as Resource<ViewActionAvailabilityFunction<MyReceipt>>
  },
  viewlet: {
    CompensationTable: '' as Ref<Viewlet>,
    LevelTable: '' as Ref<Viewlet>,
    PositionTable: '' as Ref<Viewlet>,
    PayoutTable: '' as Ref<Viewlet>,
    QuarterScoreTable: '' as Ref<Viewlet>,
    AccountTable: '' as Ref<Viewlet>,
    ReceiptTable: '' as Ref<Viewlet>,
    DepartmentTable: '' as Ref<Viewlet>,
    TransactionTable: '' as Ref<Viewlet>,
    TransactionCategoryTable: '' as Ref<Viewlet>
  },
  extension: {
    PersonCompensation: '' as ComponentExtensionId
  },
  component: {
    WebAppFrame: '' as AnyComponent,
    Workers: '' as AnyComponent,
    CompensationPanel: '' as AnyComponent,
    CompensationPresenter: '' as AnyComponent,
    CompensationPositionEditor: '' as AnyComponent,
    LevelsView: '' as AnyComponent,
    CompensationLevelEditor: '' as AnyComponent,
    CreateCompensation: '' as AnyComponent,
    LevelPanel: '' as AnyComponent,
    LevelPresenter: '' as AnyComponent,
    LevelRefPresenter: '' as AnyComponent,
    CreateLevel: '' as AnyComponent,
    PositionPanel: '' as AnyComponent,
    DepartmentPanel: '' as AnyComponent,
    RolePanel: '' as AnyComponent,
    PositionPresenter: '' as AnyComponent,
    PositionRefPresenter: '' as AnyComponent,
    RoleRefPresenter: '' as AnyComponent,
    CreatePosition: '' as AnyComponent,
    CreateRole: '' as AnyComponent,
    TeamPanel: '' as AnyComponent,
    CreateTeam: '' as AnyComponent,
    TeamsView: '' as AnyComponent,
    MoneyPresenter: '' as AnyComponent,
    KpiPresenter: '' as AnyComponent,
    MyCompensationSection: '' as AnyComponent,
    ProfileCompletionGate: '' as AnyComponent,
    SetEmployeePassword: '' as AnyComponent,
    DateRangePicker: '' as AnyComponent,
    CreateDepartment: '' as AnyComponent,
    DepartmentPresenter: '' as AnyComponent,
    DepartmentRefPresenter: '' as AnyComponent,
    EmployeeRoleEditor: '' as AnyComponent,
    EmployeeAccountRolePresenter: '' as AnyComponent,
    EmployeeJobPositionPresenter: '' as AnyComponent,
    SubmitReceipt: '' as AnyComponent,
    SoumPresenter: '' as AnyComponent,
    ReceiptPanel: '' as AnyComponent,
    ReceiptStatusPresenter: '' as AnyComponent,
    CreateFinanceAccount: '' as AnyComponent,
    CreateTransaction: '' as AnyComponent,
    CreateTransactionCategory: '' as AnyComponent,
    FinanceAccountPresenter: '' as AnyComponent,
    FinanceAccountRefPresenter: '' as AnyComponent,
    AccountBalancePresenter: '' as AnyComponent,
    TransactionKindPresenter: '' as AnyComponent,
    TransactionAmountPresenter: '' as AnyComponent,
    TransactionCategoryPresenter: '' as AnyComponent,
    TransactionCategoryRefPresenter: '' as AnyComponent,
    ActivityView: '' as AnyComponent,
    MyWorkView: '' as AnyComponent,
    TeamView: '' as AnyComponent,
    // The pages of decision 11.
    SettingsView: '' as AnyComponent
  },
  string: {
    KPIApplication: '' as IntlString,
    Workers: '' as IntlString,
    Level: '' as IntlString,
    NotSelected: '' as IntlString,
    Edit: '' as IntlString,
    Levels: '' as IntlString,
    LevelCode: '' as IntlString,
    LevelName: '' as IntlString,
    Rank: '' as IntlString,
    Position: '' as IntlString,
    Positions: '' as IntlString,
    JobRoleInUse: '' as IntlString,
    PlatformRole: '' as IntlString,
    PlatformRoles: '' as IntlString,
    CreatePlatformRole: '' as IntlString,
    ProjectRole: '' as IntlString,
    AccessLevel: '' as IntlString,
    AccessLevelHint: '' as IntlString,
    LastOwnerLevel: '' as IntlString,
    AccessLevelManagedInKpi: '' as IntlString,
    TeamPerformance: '' as IntlString,
    AccountsAndCategories: '' as IntlString,
    AllAccounts: '' as IntlString,
    AllCategories: '' as IntlString,
    AllKinds: '' as IntlString,
    Archive: '' as IntlString,
    Restore: '' as IntlString,
    BalanceToDateHint: '' as IntlString,
    BalanceTotal: '' as IntlString,
    CashOnHand: '' as IntlString,
    ConfirmDelete: '' as IntlString,
    EditTransaction: '' as IntlString,
    ExpectedThisMonth: '' as IntlString,
    Finance: '' as IntlString,
    FreeCash: '' as IntlString,
    FreeCashHint: '' as IntlString,
    FromReceipt: '' as IntlString,
    KpiThisQuarter: '' as IntlString,
    NotScoredYet: '' as IntlString,
    PaidDirectly: '' as IntlString,
    PaidFromAccount: '' as IntlString,
    PaidThisMonth: '' as IntlString,
    PayAndKpi: '' as IntlString,
    PayMonth: '' as IntlString,
    Payroll: '' as IntlString,
    PayrollCategoryHint: '' as IntlString,
    PayrollExpected: '' as IntlString,
    PayrollOnly: '' as IntlString,
    PayrollOwed: '' as IntlString,
    PayrollPaid: '' as IntlString,
    PayrollPaidHint: '' as IntlString,
    PayrollReturnedHint: '' as IntlString,
    EntryNoEmployee: '' as IntlString,
    EntryNotPayroll: '' as IntlString,
    NoPayrollExpenseCategory: '' as IntlString,
    NoPayrollIncomeCategory: '' as IntlString,
    MonthRateNote: '' as IntlString,
    SalaryHasHistory: '' as IntlString,
    SpotBonusOutsidePay: '' as IntlString,
    QuarterNotFull: '' as IntlString,
    PayrollThisMonth: '' as IntlString,
    RecordedInLedger: '' as IntlString,
    RecordPayment: '' as IntlString,
    RecordReturn: '' as IntlString,
    Returned: '' as IntlString,
    RunningBalance: '' as IntlString,
    SalaryRecord: '' as IntlString,
    Statement: '' as IntlString,
    StillOwed: '' as IntlString,
    ViaReceipts: '' as IntlString,
    Last12Months: '' as IntlString,
    ReceivedHint: '' as IntlString,
    NoPaymentsThisMonth: '' as IntlString,
    PendingReview: '' as IntlString,
    BalanceAll: '' as IntlString,
    BalanceOwed: '' as IntlString,
    BalanceSettled: '' as IntlString,
    BalanceHasPending: '' as IntlString,
    ReviewQueue: '' as IntlString,
    History: '' as IntlString,
    SearchEmployee: '' as IntlString,
    AllTeams: '' as IntlString,
    AllDepartments: '' as IntlString,
    LastPayment: '' as IntlString,
    ReviewedBy: '' as IntlString,
    SpaceAdminsManagedInKpi: '' as IntlString,
    Permissions: '' as IntlString,
    NoPermissions: '' as IntlString,
    RoleMembers: '' as IntlString,
    NoRoleMembers: '' as IntlString,
    AddRoleMembers: '' as IntlString,
    RemoveFromRole: '' as IntlString,
    RemoveFromRoleConfirm: '' as IntlString,
    RoleNoFallback: '' as IntlString,
    PermManageCatalog: '' as IntlString,
    PermManageCatalogDescription: '' as IntlString,
    PermEditCompensation: '' as IntlString,
    PermEditCompensationDescription: '' as IntlString,
    PermApproveReceipts: '' as IntlString,
    PermApproveReceiptsDescription: '' as IntlString,
    PermManageFinance: '' as IntlString,
    PermManageFinanceDescription: '' as IntlString,
    Members: '' as IntlString,
    AddMember: '' as IntlString,
    RemoveMember: '' as IntlString,
    NoMembers: '' as IntlString,
    SelectEmployee: '' as IntlString,
    Teams: '' as IntlString,
    Team: '' as IntlString,
    CreateTeam: '' as IntlString,
    TeamName: '' as IntlString,
    TeamLead: '' as IntlString,
    NoTeams: '' as IntlString,
    TeamInUse: '' as IntlString,
    Performance: '' as IntlString,
    AvgKpi: '' as IntlString,
    LatestScore: '' as IntlString,
    NoScore: '' as IntlString,
    Department: '' as IntlString,
    Description: '' as IntlString,
    Active: '' as IntlString,
    Employee: '' as IntlString,
    Compensation: '' as IntlString,
    BaseSalary: '' as IntlString,
    BonusPct: '' as IntlString,
    EffectiveFrom: '' as IntlString,
    SalaryMin: '' as IntlString,
    SalaryMax: '' as IntlString,
    BonusMin: '' as IntlString,
    BonusMax: '' as IntlString,
    SpotBonusMin: '' as IntlString,
    SpotBonusMax: '' as IntlString,
    KpiMin: '' as IntlString,
    KpiMax: '' as IntlString,
    ReviewMonths: '' as IntlString,
    UnlockAtEmployees: '' as IntlString,
    Notes: '' as IntlString,
    Locked: '' as IntlString,
    Kpi: '' as IntlString,
    KpiScore: '' as IntlString,
    QuarterScores: '' as IntlString,
    Quarter: '' as IntlString,
    Year: '' as IntlString,
    Month: '' as IntlString,
    QuarterlyBonus: '' as IntlString,
    SpotBonus: '' as IntlString,
    SpotBonuses: '' as IntlString,
    Amount: '' as IntlString,
    Reason: '' as IntlString,
    Payouts: '' as IntlString,
    Expected: '' as IntlString,
    Paid: '' as IntlString,
    Difference: '' as IntlString,
    PaidAt: '' as IntlString,
    Note: '' as IntlString,
    Settings: '' as IntlString,
    NavGroupCompany: '' as IntlString,
    NavGroupMoney: '' as IntlString,
    ExchangeRate: '' as IntlString,
    UsdToUzs: '' as IntlString,
    CreateLevel: '' as IntlString,
    CreatePosition: '' as IntlString,
    AddWorker: '' as IntlString,
    MyCompensation: '' as IntlString,
    NoCompensation: '' as IntlString,
    CompleteYourProfile: '' as IntlString,
    ChoosePosition: '' as IntlString,
    ChoosePositionDescription: '' as IntlString,
    Continue: '' as IntlString,
    LevelLocked: '' as IntlString,
    LevelInUse: '' as IntlString,
    PositionInUse: '' as IntlString,
    OutOfLevelRange: '' as IntlString,
    Money: '' as IntlString,
    Accounts: '' as IntlString,
    Account: '' as IntlString,
    AccountKind: '' as IntlString,
    Cash: '' as IntlString,
    Bank: '' as IntlString,
    Card: '' as IntlString,
    OpeningBalance: '' as IntlString,
    Balance: '' as IntlString,
    Archived: '' as IntlString,
    Transactions: '' as IntlString,
    Transaction: '' as IntlString,
    Income: '' as IntlString,
    Expense: '' as IntlString,
    Expenses: '' as IntlString,
    CashFlow: '' as IntlString,
    ThisMonthIncome: '' as IntlString,
    Kind: '' as IntlString,
    Category: '' as IntlString,
    Categories: '' as IntlString,
    OccurredAt: '' as IntlString,
    CreateAccount: '' as IntlString,
    CreateTransaction: '' as IntlString,
    CreateCategory: '' as IntlString,
    TotalIncome: '' as IntlString,
    TotalExpense: '' as IntlString,
    NetBalance: '' as IntlString,
    AccountInUse: '' as IntlString,
    CategoryInUse: '' as IntlString,
    NoTransactions: '' as IntlString,
    Overview: '' as IntlString,
    CompanyOverview: '' as IntlString,
    MyWork: '' as IntlString,
    GreetingMorning: '' as IntlString,
    GreetingDay: '' as IntlString,
    GreetingEvening: '' as IntlString,
    OpenTasksCount: '' as IntlString,
    HomeSummaryDay: '' as IntlString,
    HomeSummaryWeek: '' as IntlString,
    HomeSummaryMonth: '' as IntlString,
    HomePeriodDay: '' as IntlString,
    HomePeriodWeek: '' as IntlString,
    HomePeriodMonth: '' as IntlString,
    HomeOpenTasks: '' as IntlString,
    HomeAddedToday: '' as IntlString,
    HomeNoneAddedToday: '' as IntlString,
    HomeDueDay: '' as IntlString,
    HomeDueWeek: '' as IntlString,
    HomeDueMonth: '' as IntlString,
    HomeOverdue: '' as IntlString,
    HomeNoOverdue: '' as IntlString,
    HomeKpiQuarter: '' as IntlString,
    HomeKpiNone: '' as IntlString,
    HomePoints: '' as IntlString,
    HomePrevQuarter: '' as IntlString,
    HomeNoScore: '' as IntlString,
    HomePay: '' as IntlString,
    HomeShowFigures: '' as IntlString,
    HomeHideFigures: '' as IntlString,
    HomeTasksDay: '' as IntlString,
    HomeTasksWeek: '' as IntlString,
    HomeTasksMonth: '' as IntlString,
    HomeAll: '' as IntlString,
    HomeNoTasks: '' as IntlString,
    HomeAgendaDay: '' as IntlString,
    HomeAgendaWeek: '' as IntlString,
    HomeAgendaMonth: '' as IntlString,
    HomeNoMeetings: '' as IntlString,
    HomeJoin: '' as IntlString,
    HomeAllDay: '' as IntlString,
    HomeInbox: '' as IntlString,
    HomeOpen: '' as IntlString,
    HomeAllRead: '' as IntlString,
    HomeDueToday: '' as IntlString,
    HomeDueTomorrow: '' as IntlString,
    HomeOverdueDays: '' as IntlString,
    Sprint: '' as IntlString,
    MyLoad: '' as IntlString,
    RemainingThisSprint: '' as IntlString,
    InProgress: '' as IntlString,
    DoneThisSprint: '' as IntlString,
    MySprintIssues: '' as IntlString,
    MyKpiScore: '' as IntlString,
    IssuesCompleted: '' as IntlString,
    WorkingDaysLeft: '' as IntlString,
    OfCapacity: '' as IntlString,
    TargetOrFewer: '' as IntlString,
    CapacityWarning: '' as IntlString,
    MessageLead: '' as IntlString,
    Viewing: '' as IntlString,
    Assigned: '' as IntlString,
    Available: '' as IntlString,
    WorkTeams: '' as IntlString,
    WorkProjects: '' as IntlString,
    WorkTasks: '' as IntlString,
    Deadline: '' as IntlString,
    Overdue: '' as IntlString,
    NoOpenTasks: '' as IntlString,
    NoTeamsYet: '' as IntlString,
    IssueCol: '' as IntlString,
    TitleCol: '' as IntlString,
    EstimateCol: '' as IntlString,
    Hours: '' as IntlString,
    Until: '' as IntlString,
    Stale: '' as IntlString,
    MyTeam: '' as IntlString,
    NotATeamLead: '' as IntlString,
    LoadVsCapacity: '' as IntlString,
    LoadVsCapacityHint: '' as IntlString,
    LegendWarn: '' as IntlString,
    LegendBad: '' as IntlString,
    MoveSuggestion: '' as IntlString,
    NeedsAttention: '' as IntlString,
    NothingStuck: '' as IntlString,
    DaysShort: '' as IntlString,
    OverEstimateReason: '' as IntlString,
    InStatusReason: '' as IntlString,
    KpiCaption: '' as IntlString,
    YourSummary: '' as IntlString,
    Headcount: '' as IntlString,
    SalaryFund: '' as IntlString,
    BudgetRemaining: '' as IntlString,
    ThisMonth: '' as IntlString,
    ThisYear: '' as IntlString,
    TopEarners: '' as IntlString,
    MonthlyTotal: '' as IntlString,
    NoDataYet: '' as IntlString,
    Receipts: '' as IntlString,
    Receipt: '' as IntlString,
    SubmitReceipt: '' as IntlString,
    Pending: '' as IntlString,
    Approved: '' as IntlString,
    Rejected: '' as IntlString,
    Status: '' as IntlString,
    Received: '' as IntlString,
    Period: '' as IntlString,
    Screenshot: '' as IntlString,
    AddProof: '' as IntlString,
    Approve: '' as IntlString,
    Reject: '' as IntlString,
    ReviewNote: '' as IntlString,
    SubmittedAt: '' as IntlString,
    ReviewedAt: '' as IntlString,
    NoReceipts: '' as IntlString,
    ReceiptExists: '' as IntlString,
    Shortfall: '' as IntlString,
    AllReceipts: '' as IntlString,
    Employees: '' as IntlString,
    People: '' as IntlString,
    Departments: '' as IntlString,
    CreateDepartment: '' as IntlString,
    RoleUser: '' as IntlString,
    RoleMaintainer: '' as IntlString,
    RoleOwner: '' as IntlString,
    RoleGuest: '' as IntlString,
    DepartmentInUse: '' as IntlString,
    NoEmployees: '' as IntlString,
    NotMember: '' as IntlString,
    AddPayment: '' as IntlString,
    AlreadySubmitted: '' as IntlString,
    Remaining: '' as IntlString,
    Overpaid: '' as IntlString,
    MoveSuggestionPartial: '' as IntlString,
    AccountsWithAccess: '' as IntlString,
    SalaryAccessHint: '' as IntlString,
    SalaryAccess: '' as IntlString,
    ExchangeRateHint: '' as IntlString,
    NoEstimateReason: '' as IntlString,
    NoSprintWork: '' as IntlString,
    OpenAssignedIssues: '' as IntlString,
    ChangeFromMonth: '' as IntlString,
    ChangeFromMonthHint: '' as IntlString,
    SalaryHistory: '' as IntlString,
    ApproveWouldOverpay: '' as IntlString,
    NoPositionsYet: '' as IntlString,
    KpiCaptionOther: '' as IntlString,
    CapacityWarningOther: '' as IntlString,
    SprintIssues: '' as IntlString,
    Load: '' as IntlString,
    OtherPayments: '' as IntlString,
    NoRate: '' as IntlString,
    LastMonth: '' as IntlString,
    ThisQuarter: '' as IntlString,
    LastYear: '' as IntlString,
    AllTime: '' as IntlString,
    CustomRange: '' as IntlString,
    Columns: '' as IntlString,
    Total: '' as IntlString,
    PayoutRequired: '' as IntlString,
    MustReturn: '' as IntlString,
    ExpensesAndBudget: '' as IntlString,
    ThisMonthExpense: '' as IntlString,
    Budget: '' as IntlString,
    IncomeFromEmployees: '' as IntlString,
    Salaries: '' as IntlString,
    MonthlyIncomeExpense: '' as IntlString,
    DoubleClickToEdit: '' as IntlString,
    NoExpenses: '' as IntlString,
    NoAccount: '' as IntlString,
    Add: '' as IntlString,
    Delete: '' as IntlString,
    Confirm: '' as IntlString,
    ExpensesByCategory: '' as IntlString,
    BudgetFormula: '' as IntlString,
    Calculation: '' as IntlString,
    SalaryFundNote: '' as IntlString,
    Activity: '' as IntlString,
    Joined: '' as IntlString,
    LastSession: '' as IntlString,
    Online: '' as IntlString,
    Offline: '' as IntlString,
    AllEmployeesHaveRecord: '' as IntlString,
    TeamNameTaken: '' as IntlString,
    LevelCodeTaken: '' as IntlString,
    EditReceipt: '' as IntlString,
    ReceiptLocked: '' as IntlString,
    WithdrawReceipt: '' as IntlString,
    WithdrawReceiptConfirm: '' as IntlString,
    FileTooLarge: '' as IntlString,
    UploadFailed: '' as IntlString,
    ScreenshotOrPdf: '' as IntlString,
    OpenFile: '' as IntlString,
    NoLedgerForApproval: '' as IntlString,
    PayrollFlagTitle: '' as IntlString,
    PayrollFlagOnExpense: '' as IntlString,
    PayrollFlagOnIncome: '' as IntlString,
    PayrollFlagOff: '' as IntlString,
    ArchiveAccountConfirm: '' as IntlString,
    RateChangeTitle: '' as IntlString,
    RateChangeConfirm: '' as IntlString,
    PayMonthPick: '' as IntlString,
    OccurredAtDetail: '' as IntlString,
    PayStart: '' as IntlString,
    PayEnd: '' as IntlString,
    PayEndNone: '' as IntlString,
    PayEndClear: '' as IntlString,
    PayDatesHint: '' as IntlString,
    InactiveNoPayEnd: '' as IntlString,
    PayStartConfirm: '' as IntlString,
    PayEndConfirm: '' as IntlString,
    AddSpotBonus: '' as IntlString,
    SpotBonusReason: '' as IntlString,
    SpotBonusMonth: '' as IntlString,
    SpotBonusNone: '' as IntlString,
    SpotBonusKeptHint: '' as IntlString,
    SetPassword: '' as IntlString,
    HolidayPay: '' as IntlString,
    HolidayPayCheck: '' as IntlString,
    HolidayPayHint: '' as IntlString,
    HolidayPayApproved: '' as IntlString,
    NoHolidayCategory: '' as IntlString,
    SetPasswordHint: '' as IntlString,
    NewPassword: '' as IntlString,
    PasswordTooShort: '' as IntlString,
    PasswordSet: '' as IntlString,
    PasswordNoAccount: '' as IntlString,
    KpiBandHint: '' as IntlString,
    KpiClamped: '' as IntlString,
    NotSet: '' as IntlString,
    PayEndBeforeStart: '' as IntlString,
    LevelUnlockHint: '' as IntlString,
    QuarterLabel: '' as IntlString,
    QuarterShort: '' as IntlString,
    WorkOf: '' as IntlString,
    PreviousYear: '' as IntlString,
    NextYear: '' as IntlString,
    ShowNumbers: '' as IntlString,
    Hide: '' as IntlString,
    AutoHide: '' as IntlString,
    UntilHidden: '' as IntlString,
    WebApp: '' as IntlString,
    PayoutMark: '' as IntlString,
    WebappRecord: '' as IntlString,
    Key: '' as IntlString,
    Value: '' as IntlString,
    ExpiresAt: '' as IntlString,
    ClaimedBy: '' as IntlString,
    ConfirmedBy: '' as IntlString,
    AfterMinutes: '' as IntlString,
    OtherMinutes: '' as IntlString,
    Minutes: '' as IntlString,
    MoneyHiddenHint: '' as IntlString,
    PayComponent: '' as IntlString,
    ComponentSalary: '' as IntlString,
    ComponentQuarterBonus: '' as IntlString,
    ComponentPremiya: '' as IntlString,
    ComponentHoliday: '' as IntlString,
    ComponentNone: '' as IntlString,
    ToDate: '' as IntlString,
    NextMonth: '' as IntlString,
    ExpectedSoum: '' as IntlString,
    PaidSoum: '' as IntlString,
    BalanceSoum: '' as IntlString,
    BalanceLegend: '' as IntlString,
    AmountSoum: '' as IntlString,
    IncludingHoliday: '' as IntlString,
    NotPay: '' as IntlString,
    NotPayNotOwed: '' as IntlString,
    MonthTableHint: '' as IntlString,
    ToDateTableHint: '' as IntlString,
    EmployeeStatement: '' as IntlString,
    ExpectedToDate: '' as IntlString,
    MonthsCount: '' as IntlString,
    OfWhichCurrentMonth: '' as IntlString,
    OwedPastMonths: '' as IntlString,
    ByComponentToDate: '' as IntlString,
    Months: '' as IntlString,
    Owed: '' as IntlString,
    CurrentMonth: '' as IntlString,
    Calculated: '' as IntlString,
    NotScoredAs100: '' as IntlString,
    QuarterNotFullShort: '' as IntlString,
    ActiveEmployees: '' as IntlString,
    AllEmployees: '' as IntlString,
    RevealToEdit: '' as IntlString,
    SavesInPlace: '' as IntlString,
    KpiCellHint: '' as IntlString,
    PremiyaSoum: '' as IntlString,
    Payments: '' as IntlString,
    AccountSection: '' as IntlString,
    ReceiptAs: '' as IntlString,
    NoCategoryOfComponent: '' as IntlString,
    ComponentLocked: '' as IntlString,
    ComponentFixed: '' as IntlString,
    EditCategory: '' as IntlString,
    Entries: '' as IntlString,
    Sections: '' as IntlString,
    Rate: '' as IntlString,
    CurrentRate: '' as IntlString,
    ChangeRate: '' as IntlString,
    NoPendingReceipts: '' as IntlString,
    OpenStatement: '' as IntlString,
    MyReceipts: '' as IntlString,
    MyQuarterScores: '' as IntlString,
    ThisMonthExpected: '' as IntlString,
    ReceivedToDate: '' as IntlString,
    NotPaidYet: '' as IntlString,
    Fund: '' as IntlString,
    CompanyOwes: '' as IntlString,
    ByCategory: '' as IntlString,
    ComponentHint: '' as IntlString,
    NeverLoggedIn: '' as IntlString
  }
})

export default kpi
